/* eslint-disable indent */
import { Anchor, FieldController, NameValidator, NumbersValidator, TextField } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../../overmind';
import { SkeletonLoader } from '../../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { AssessmentRequest } from '../../../../overmind/actions';
import { AssessmentStatus } from '../../../../enums/assessment-status';

export function AssessmentDetailsWidget({ details, isLoading, onSaveAndContinue }: { details: AssessmentRequest; isLoading: boolean; onSaveAndContinue: (details: AssessmentRequest) => void }): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { watch, control, trigger, setValue } = useForm({ mode: 'onBlur' });

  const [loading, setLoading] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<FieldValues>();

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  useEffect(() => {
    if (details) {
      setValue('name', details.name || '');
      setValue('points', details.keplerPointsAvailable?.toString() || '0');
    }
  }, [details]);

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  // @Jedidja
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 15 }}>
          <SkeletonLoader height="45px" />

          <SkeletonLoader height="45px" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <SkeletonLoader width="150px" height="25px" />
        </div>
      </div>
    );
  }

  return (
    <form id="assessmentDetails">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 15 }}>
        <FieldController
          control={control}
          name="name"
          rules={new NameValidator()}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="text"
              label="Name"
              responsive
              validation={{
                dirty: fieldState.isDirty || !!fieldState.error,
                invalid: !!fieldState.error,
                message: fieldState.error?.message ?? 'Enter a name',
              }}
            />
          )}
        />

        <FieldController
          control={control}
          name="points"
          rules={new NumbersValidator()}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="number"
              label="Kepler Points"
              validation={{
                dirty: fieldState.isDirty || !!fieldState.error,
                invalid: !!fieldState.error,
                message: fieldState.error?.message ?? 'Enter a number',
              }}
            />
          )}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Anchor
          arrow
          onClick={async () => {
            if (!fieldValues?.name || !fieldValues.points) return trigger(['name', 'points']);

            setLoading(true);

            const request: AssessmentRequest = {
              companySlug: companyVariables.slug!,
              slug: details.slug,
              assessmentType: details.assessmentType,
              assessmentStatus: details.assessmentStatus ?? AssessmentStatus.Draft,
              name: fieldValues.name,
              keplerPointsAvailable: parseInt(fieldValues.points as string) || 0,
              description: details.description,
              minScore: details.minScore,
              maxScore: details.maxScore,
              allowReassessment: details.allowReassessment,
              forceAssessment: details.forceAssessment,
              assessmentSubType: details.assessmentSubType,
            };

            // If we have a slug, we're editing an existing assessment
            const response = await actions.updateAssessment(request);

            setLoading(false);

            !response
              ? actions.addNotification({
                  active: true,
                  id: crypto.randomUUID(),
                  type: NotificationType.Error,
                  priority: NotificationPriority.Toast,
                  title: 'Something went wrong saving these details',
                  message: 'Please try again',
                  toastIncludeIcon: true,
                })
              : onSaveAndContinue({ ...request, slug: response });
          }}
        >
          Save & Continue
        </Anchor>
      </div>
    </form>
  );
}
